import { PUB_FORMATS } from "../../lib/domain";
import { knownFormat, readDirectionPrefs, rememberDirectionPrefs } from "../../lib/directionPrefs";
import { DueDateTime } from "../../components/DueDateTime";
import { Plus } from "lucide-react";
import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Modal } from "../../components/ui/modal";
import { FilterChip } from "../../components/ui/primitives";
import { useProjectAccounts } from "../../lib/accounts";
import { uid } from "../../lib/utils";
import { byPeople, DEPT_NAME, GROUP_OF, homeProject, ME_EDITOR, rememberProject, PEOPLE, PROJECTS, projectPublishesAfterModeration } from "../../data/people";
import { at, dayOf, DAYS } from "../../lib/time";
import { useNow } from "../../lib/useNow";
import { notify } from "../../lib/notify";
import { useStore } from "../../store";
import { BatchNewTasks } from "../client/BatchNewTasks";

/**
 * «Своя публикация»: монтажёр приносит её сам и ставит только себе.
 *
 * Дальше всё как у назначенной — модератор, потом контент-мейкер его группы,
 * Telegram и ссылка. Проект — тот, где работает группа монтажёра; если их
 * несколько, он выбирает.
 */
export function OwnTopicButton() {
  const [open, setOpen] = useState(false);
  const openTask = useStore((s) => s.openTask);
  const setIntent = useStore((s) => s.setIntent);
  // Поставил себе — сразу окно сдачи (02.11.2026): раньше публикацию
  // приходилось искать в списке, чтобы загрузить ролик.
  const openUpload = (ids: string[]) => {
    if (ids.length !== 1) return;
    setIntent({ id: ids[0], mode: "upload" });
    window.setTimeout(() => openTask(ids[0], "etask"), 220);
  };
  // Несколько своих публикаций за раз (09.10.2026) — то же окно-список, что у контент-мейкера.
  const [batch, setBatch] = useState<{ title: string; format: string; brief: string } | null>(null);
  const close = () => { setOpen(false); setBatch(null); };
  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus />
        Своя публикация
      </Button>
      <Modal open={open} onClose={close} width={batch ? 1100 : 560} height={batch ? Math.min(780, window.innerHeight - 48) : undefined}>
        {open && (batch
          ? <BatchNewTasks own first={batch} onClose={close} onCreated={openUpload} />
          : <OwnTopicForm onClose={close} onBatch={setBatch} onCreated={openUpload} />)}
      </Modal>
    </>
  );
}

function OwnTopicForm({ onClose, onBatch, onCreated }: { onClose: () => void; onBatch: (first: { title: string; format: string; brief: string }) => void; onCreated: (ids: string[]) => void }) {
  const now = useNow();
  const createTask = useStore((s) => s.createTask);
  const group = GROUP_OF[ME_EDITOR];
  // Направления, где монтажёр состоит сам; у старых, без людей, — по команде.
  const projects = PROJECTS.filter((p) => (byPeople(p) ? p.editorIds!.includes(ME_EDITOR) : Boolean(group && p.groupIds.includes(group))));
  const [projectId, setProjectId] = useState(homeProject(projects, group)?.id ?? "");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  // Прошлый выбор в направлении (02.11.2026): формат, аккаунт, автопубликация.
  const [format, setFormat] = useState<string>(() => knownFormat(readDirectionPrefs(projectId).format));
  const today = dayOf(now);
  const firstDay = DAYS.find((d) => d.idx >= today && at(d.idx, 20) > now)?.idx ?? today;
  // Срок — как у постановщика: дата и время (раньше только день, до 20:00).
  const [due, setDue] = useState(() => at(firstDay, 20));
  const workDay = dayOf(due);

  const project = projects.find((p) => p.id === projectId);
  // Одна публикация — один аккаунт (08.10.2026).
  const accounts = useProjectAccounts(projectId);
  const [accountId, setAccountId] = useState<string | null>(() => readDirectionPrefs(projectId).accountIds?.[0] ?? null);
  const account = accounts.find((a) => a.id === accountId) ?? accounts[0] ?? null;
  // Без аккаунта ролик никуда не выйдет; у «ИИ-контента» аккаунтов нет вовсе — там можно.
  const ready = title.trim().length >= 3 && !!project && (!!account || !project.accountIds?.length) && due > now;
  // В «Президенте» после «Норм» ролик всегда уходит в Instagram сам — галочку не снять (28.10.2026).
  const [autoPublishChoice, setAutoPublishInstagram] = useState<boolean | null>(() => readDirectionPrefs(projectId).autoPublish ?? null);
  const changeProject = (id: string) => {
    const prefs = readDirectionPrefs(id);
    setProjectId(id);
    setAccountId(prefs.accountIds?.[0] ?? null);
    setAutoPublishInstagram(prefs.autoPublish ?? null);
  };
  const afterModeration = projectPublishesAfterModeration(projectId);
  const autoPublishInstagram = afterModeration || (autoPublishChoice ?? false);

  const submit = () => {
    if (!ready) return;
    rememberProject(project!.id);
    rememberDirectionPrefs(project!.id, { accountIds: account ? [account.id] : undefined, format, autoPublish: autoPublishChoice ?? undefined });
    const id = createTask({
      title: title.trim(),
      description: description.trim(),
      period: "day",
      dept: DEPT_NAME[project!.divisionId] ?? PEOPLE[ME_EDITOR].dept ?? "",
      projectId: project!.id,
      autoPublishInstagram,
      // Публикация — ровно один ролик с тем же названием.
      items: [{
        id: uid(), title: title.trim(), format, brief: description.trim(), files: [],
        accountId: account?.id ?? null, status: "assigned", round: 1, versions: [], revisions: [], poster: "",
      }],
      creator: ME_EDITOR,
      editor: ME_EDITOR,
      day: workDay,
      estimate: 2,
      due,
    });
    onClose();
    onCreated([id]);
    notify(`Своя публикация · «${title.trim()}» — в работе, можно сдавать`, { icon: "check" });
  };

  return (
    <div className="flex flex-col gap-4 px-7 pb-6 pt-6">
      <h2 className="text-[22px] font-semibold tracking-[-0.02em]">Своя публикация</h2>
      {projects.length === 0 ? (
        <p className="text-sm text-muted-foreground">Вы не состоите ни в одном направлении — попросите руководителя отдела.</p>
      ) : (
        <>
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Название — например, «Очереди в ЦОН перед праздниками»"
            className="h-11 w-full rounded-full border border-input bg-background px-4 text-sm shadow-mxs outline-none placeholder:text-muted-foreground focus:ring-4 focus:ring-ring/20"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Что хотите снять — коротко, чтобы контент-мейкер понял идею"
            className="w-full resize-none rounded-[20px] border border-input bg-background px-[18px] py-3 text-sm leading-relaxed shadow-mxs outline-none placeholder:text-muted-foreground focus:ring-4 focus:ring-ring/20"
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Формат</span>
            {PUB_FORMATS.map((f) => (
              <FilterChip key={f.value} active={f.value === format} onClick={() => setFormat(f.value)}>{f.label}</FilterChip>
            ))}
          </div>
          {projects.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">Направление</span>
              {projects.map((p) => (
                <FilterChip key={p.id} active={p.id === projectId} onClick={() => changeProject(p.id)}>{p.name}</FilterChip>
              ))}
            </div>
          )}
          {accounts.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">Аккаунт</span>
              {accounts.map((a) => (
                <FilterChip key={a.id} active={a.id === account?.id} onClick={() => setAccountId(a.id)}>{a.name}</FilterChip>
              ))}
            </div>
          )}
          {account?.platform === "instagram" && (
            <label className="flex cursor-pointer items-center gap-2 text-[13px]">
              <input type="checkbox" checked={autoPublishInstagram} disabled={afterModeration} onChange={(e) => setAutoPublishInstagram(e.target.checked)} className="size-4 accent-primary disabled:opacity-100" />
              Опубликовать в {account.name} после {afterModeration ? "модерации" : "приёмки"}
            </label>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Срок</span>
            <DueDateTime due={due} now={now} onChange={setDue} />
            {due <= now && <span className="text-[13px] text-rework-fg">срок уже прошёл</span>}
          </div>
        </>
      )}
      <div className="flex justify-end gap-2.5 pt-1">
        {projects.length > 0 && (
          <Button variant="ghost" className="mr-auto" onClick={() => onBatch({ title, format, brief: description })}>
            <Plus />
            Несколько
          </Button>
        )}
        <Button variant="outline" onClick={onClose}>Отмена</Button>
        <Button onClick={submit} disabled={!ready}>Взять в работу</Button>
      </div>
    </div>
  );
}
